import { UserRole } from '@repo/auth/definitions/user-role';

import { FeatureFlag } from './feature-flag';

// Legacy routes kept for backward compatibility and redirects.
export const LEGACY_CONSTRAINTS_PAGE_ROUTE = '/constraints' as const;
export const LEGACY_CONSTRAINT_PAGE_ROUTE = '/constraint' as const;
export const LEGACY_FRAME_LISTS_PAGE_ROUTE = '/frame-lists' as const;
export const LEGACY_FRAME_LIST_PAGE_ROUTE = '/frame-list' as const;

export const ACCOUNTS_PAGE_LABEL = 'Accounts' as const;
export const ASSET_MANAGER_PAGE_LABEL = 'Asset manager' as const;
export const AUTOMATED_CAMPAIGNS_PAGE_LABEL = 'Automated campaigns' as const;
export const AUTOMATED_CAMPAIGNS_PAGE_ROUTE = '/automated/campaigns' as const;
export const CAMPAIGNS_PAGE_LABEL = 'Campaigns' as const;
export const CAMPAIGNS_PAGE_ROUTE = '/campaigns' as const;
export const CONTENT_CAMPAIGNS_PAGE_LABEL = 'Content campaigns' as const;
export const CONTENT_CAMPAIGNS_PAGE_ROUTE = '/content/content-campaigns' as const;
export const CONTENT_NEW_ALLOCATION_PAGE_LABEL = 'Content allocation' as const;
export const CONTENT_NEW_ALLOCATION_PAGE_ROUTE = '/content/content-allocation' as const;
export const CONTENTS_MODERATION_PAGE_LABEL = 'Content moderation' as const;
export const CONTENT_MODERATION_PAGE_ROUTE = '/content/content-moderation' as const;
export const CONTENT_LIBRARY_PAGE_LABEL = 'Content library' as const;
export const CONTENT_LIBRARY_PAGE_ROUTE = '/content/content-library' as const;
export const CONTENT_LINK_CAMPAIGNS_PAGE_LABEL = 'Link campaigns' as const;
export const CONTENT_LINK_CAMPAIGNS_PAGE_ROUTE = '/content/link-campaigns' as const;
export const CONTENTS_PAGE_LABEL = 'Contents' as const;
export const CONTENTS_PAGE_ROUTE = '/asset-manager/constraints-assets/contents' as const;
export const CONTENT_PAGE_ROUTE = '/asset-manager/constraint-entities/content' as const;
export const CONSTRAINTS_PAGE_LABEL = 'Constraints' as const;
export const CONSTRAINTS_ASSETS_PAGE_LABEL = 'Constraints assets' as const;
export const CONSTRAINTS_PAGE_ROUTE = '/asset-manager/constraints' as const;
export const CONSTRAINT_PAGE_ROUTE = '/asset-manager/constraint' as const;
export const CONSTRAINTS_RULES_ROUTE = CONSTRAINTS_PAGE_ROUTE;
export const CONSTRAINTS_RULE_ROUTE = CONSTRAINT_PAGE_ROUTE;
export const CONSTRAINTS_RULE_NEW_ROUTE = '/asset-manager/constraint/new' as const;
export const ACCOUNTS_PAGE_ROUTE = '/asset-manager/constraints-assets/accounts' as const;
export const ACCOUNT_PAGE_ROUTE = '/asset-manager/constraint-entities/account' as const;
export const PRODUCT_CATEGORIES_PAGE_LABEL = 'Product categories' as const;
export const PRODUCT_CATEGORIES_PAGE_ROUTE = '/asset-manager/constraints-assets/product-categories' as const;
export const PRODUCT_CATEGORY_PAGE_ROUTE = '/asset-manager/constraints-assets/product-category' as const;
export const QUOTELINE_PAGE_ROUTE = '/quoteline' as const;
export const FRAMES_PAGE_ROUTE = '/asset-manager/frames' as const;
export const FRAMES_PAGE_LABEL = 'Frames' as const;
export const FRAME_PAGE_ROUTE = '/asset-manager/frame' as const;
export const FRAME_LISTS_PAGE_ROUTE = '/asset-manager/frame-lists' as const;
export const FRAME_LIST_PAGE_ROUTE = '/asset-manager/frame-list' as const;
export const FRAME_LIST_NEW_PAGE_ROUTE = '/asset-manager/frame-list/new' as const;
export const FRAME_LISTS_PAGE_LABEL = 'Frame Lists' as const;
export const FRAME_MANAGEMENT_PAGE_ROUTE = '/frame-management' as const;
export const HOME_PAGE_LABEL = 'Home' as const;
export const HOME_PAGE_ROUTE = '/' as const;
export const RECEIVED_CONTENT_PAGE_LABEL = 'Received content' as const;
export const RECEIVED_CONTENT_PAGE_ROUTE = '/content/received-content' as const;
export const SCHEDULE_PAGE_ROUTE = '/schedule' as const;
export const UNAUTHORIZED_PAGE_LABEL = 'Unauthorised' as const;
export const UNAUTHORIZED_PAGE_ROUTE = '/unauthorised' as const;

const PAGE_ROUTES = [
  CAMPAIGNS_PAGE_ROUTE,
  FRAME_PAGE_ROUTE,
  FRAMES_PAGE_ROUTE,
  FRAME_LIST_PAGE_ROUTE,
  AUTOMATED_CAMPAIGNS_PAGE_ROUTE,
  ACCOUNT_PAGE_ROUTE,
  ACCOUNTS_PAGE_ROUTE,
  CONTENTS_PAGE_ROUTE,
  CONTENT_PAGE_ROUTE,
  CONSTRAINTS_PAGE_ROUTE,
  CONSTRAINT_PAGE_ROUTE,
  CONTENT_NEW_ALLOCATION_PAGE_ROUTE,
  CONTENT_CAMPAIGNS_PAGE_ROUTE,
  CONTENT_LIBRARY_PAGE_ROUTE,
  CONTENT_LINK_CAMPAIGNS_PAGE_ROUTE,
  CONTENT_MODERATION_PAGE_ROUTE,
  FRAME_LISTS_PAGE_ROUTE,
  HOME_PAGE_ROUTE,
  PRODUCT_CATEGORIES_PAGE_ROUTE,
  PRODUCT_CATEGORY_PAGE_ROUTE,
  QUOTELINE_PAGE_ROUTE,
  RECEIVED_CONTENT_PAGE_ROUTE,
  SCHEDULE_PAGE_ROUTE,
  UNAUTHORIZED_PAGE_ROUTE,
] as const;

export type PageRoute = (typeof PAGE_ROUTES)[number];

export type RouteConfig = {
  activeFor?: string[];
  featureFlag?: FeatureFlag;
  initialParams?: string;
  label?: string;
  isPublic?: boolean;
  isTopNavigation?: boolean;
  navigationParentLabel?: string;
  navigationParentLabels?: string[];
  requiredRoles?: UserRole[];
};

export const ROUTES: Record<PageRoute, RouteConfig> = {
  // Order is important (!)
  /* eslint-disable sort-keys */
  [HOME_PAGE_ROUTE]: {
    label: HOME_PAGE_LABEL,
    isPublic: true,
  },
  [UNAUTHORIZED_PAGE_ROUTE]: {
    isPublic: true,
    label: UNAUTHORIZED_PAGE_LABEL,
  },

  // planning
  [CAMPAIGNS_PAGE_ROUTE]: {
    featureFlag: FeatureFlag.dbd477,
    isTopNavigation: true,
    label: CAMPAIGNS_PAGE_LABEL,
    requiredRoles: [
      UserRole.Uk_Planning,
      UserRole.Uk_Planning_Readonly,
      UserRole.Uk_Planning_Advance,
      UserRole.Uk_Campaign_Maintenance,
      UserRole.Uk_Campaign_Maintenance_Readonly,
      UserRole.Uk_Swap_Advance,
      UserRole.Uk_Unlock_Frames,
    ],
    activeFor: ['/campaign'],
  },
  [AUTOMATED_CAMPAIGNS_PAGE_ROUTE]: {
    featureFlag: FeatureFlag.lpad313ShowAutomatedCampaigns,
    isTopNavigation: true,
    label: AUTOMATED_CAMPAIGNS_PAGE_LABEL,
    requiredRoles: [
      UserRole.Uk_Planning,
      UserRole.Uk_Planning_Readonly,
      UserRole.Uk_Planning_Advance,
      UserRole.Uk_Campaign_Maintenance,
      UserRole.Uk_Campaign_Maintenance_Readonly,
      UserRole.Uk_Swap_Advance,
      UserRole.Uk_Unlock_Frames,
    ],
  },
  [QUOTELINE_PAGE_ROUTE]: {
    requiredRoles: [UserRole.Uk_Planning, UserRole.Uk_Planning_Readonly, UserRole.Uk_Planning_Advance],
  },
  [SCHEDULE_PAGE_ROUTE]: {
    requiredRoles: [UserRole.Uk_Planning, UserRole.Uk_Planning_Advance],
  },

  // constraints & frame management
  [FRAMES_PAGE_ROUTE]: {
    isTopNavigation: true,
    label: FRAMES_PAGE_LABEL,
    navigationParentLabels: [ASSET_MANAGER_PAGE_LABEL],
    requiredRoles: [UserRole.Uk_Constraints, UserRole.Uk_Constraints_Readonly],
    activeFor: [FRAME_PAGE_ROUTE],
  },
  [FRAME_PAGE_ROUTE]: {
    requiredRoles: [UserRole.Uk_Constraints, UserRole.Uk_Constraints_Readonly],
  },
  [FRAME_LISTS_PAGE_ROUTE]: {
    isTopNavigation: true,
    label: FRAME_LISTS_PAGE_LABEL,
    navigationParentLabels: [ASSET_MANAGER_PAGE_LABEL],
    requiredRoles: [UserRole.Uk_Frame_List, UserRole.Uk_Frame_List_Readonly],
    activeFor: [FRAME_LIST_PAGE_ROUTE],
  },
  [FRAME_LIST_PAGE_ROUTE]: {
    requiredRoles: [UserRole.Uk_Frame_List, UserRole.Uk_Frame_List_Readonly],
  },
  [CONSTRAINTS_PAGE_ROUTE]: {
    isTopNavigation: true,
    label: CONSTRAINTS_PAGE_LABEL,
    navigationParentLabels: [ASSET_MANAGER_PAGE_LABEL],
    requiredRoles: [UserRole.Uk_Constraints, UserRole.Uk_Constraints_Readonly],
    activeFor: [CONSTRAINT_PAGE_ROUTE],
  },
  [CONSTRAINT_PAGE_ROUTE]: {
    requiredRoles: [UserRole.Uk_Constraints, UserRole.Uk_Constraints_Readonly],
  },
  [ACCOUNTS_PAGE_ROUTE]: {
    isTopNavigation: true,
    label: ACCOUNTS_PAGE_LABEL,
    navigationParentLabels: [ASSET_MANAGER_PAGE_LABEL, CONSTRAINTS_ASSETS_PAGE_LABEL],
    requiredRoles: [UserRole.Uk_Constraints, UserRole.Uk_Constraints_Readonly],
    activeFor: [ACCOUNT_PAGE_ROUTE],
  },
  [ACCOUNT_PAGE_ROUTE]: {
    requiredRoles: [UserRole.Uk_Constraints, UserRole.Uk_Constraints_Readonly],
  },
  [CONTENTS_PAGE_ROUTE]: {
    isTopNavigation: true,
    label: CONTENTS_PAGE_LABEL,
    navigationParentLabels: [ASSET_MANAGER_PAGE_LABEL, CONSTRAINTS_ASSETS_PAGE_LABEL],
    requiredRoles: [UserRole.Uk_Constraints, UserRole.Uk_Constraints_Readonly],
    activeFor: [CONTENT_PAGE_ROUTE],
  },
  [CONTENT_PAGE_ROUTE]: {
    requiredRoles: [UserRole.Uk_Constraints, UserRole.Uk_Constraints_Readonly],
  },
  [PRODUCT_CATEGORIES_PAGE_ROUTE]: {
    isTopNavigation: true,
    label: PRODUCT_CATEGORIES_PAGE_LABEL,
    navigationParentLabels: [ASSET_MANAGER_PAGE_LABEL, CONSTRAINTS_ASSETS_PAGE_LABEL],
    requiredRoles: [UserRole.Uk_Constraints, UserRole.Uk_Constraints_Readonly],
    activeFor: [PRODUCT_CATEGORY_PAGE_ROUTE],
  },
  [PRODUCT_CATEGORY_PAGE_ROUTE]: {
    requiredRoles: [UserRole.Uk_Constraints, UserRole.Uk_Constraints_Readonly],
  },

  // content
  [CONTENT_CAMPAIGNS_PAGE_ROUTE]: {
    featureFlag: FeatureFlag.dbd880,
    isTopNavigation: true,
    label: CONTENT_CAMPAIGNS_PAGE_LABEL,
    requiredRoles: [UserRole.Uk_Content_Moderate],
  },
  [CONTENT_NEW_ALLOCATION_PAGE_ROUTE]: {
    label: CONTENT_NEW_ALLOCATION_PAGE_LABEL,
    requiredRoles: [UserRole.Uk_Content_Moderate],
  },
  [RECEIVED_CONTENT_PAGE_ROUTE]: {
    initialParams: 'page=1&fileStatus=%5B%22Unmoderated%22%5D',
    isTopNavigation: true,
    label: RECEIVED_CONTENT_PAGE_LABEL,
    requiredRoles: [UserRole.Uk_Content_Moderate],
  },
  [CONTENT_LIBRARY_PAGE_ROUTE]: {
    initialParams: 'page=1',
    isTopNavigation: true,
    label: CONTENT_LIBRARY_PAGE_LABEL,
    requiredRoles: [UserRole.Uk_Content_Moderate],
  },
  [CONTENT_LINK_CAMPAIGNS_PAGE_ROUTE]: {
    featureFlag: FeatureFlag.dbd4227ToggleContentLinkCampaigns,
    label: CONTENT_LINK_CAMPAIGNS_PAGE_LABEL,
    requiredRoles: [UserRole.Uk_Content_Moderate],
  },
  [CONTENT_MODERATION_PAGE_ROUTE]: {
    label: CONTENTS_MODERATION_PAGE_LABEL,
    requiredRoles: [UserRole.Uk_Content_Moderate],
  },
  /* eslint-enable sort-keys */
} as const;
